import { Box, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { EXCLUSIVITIES_PAGE_SIZE } from "../../../common/components/functional/pagination/constants";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import { Hits } from "../../../common/types/Paginated";
import { Nullable } from "../../../common/types/nullable";
import { useTablePagination } from "../../../hooks/useTablePagination";
import { TalentExclusivity } from "../../talents/talentAPI";
import {
  addExclusivityEntry,
  fetchExclusivitiesByTalentId,
  removeExclusivityEntry,
  updateExclusivityEntry,
} from "../../talents/talentsSlice";
import { RemoveExclusivityEntry } from "../../talents/types/commands";
import AddExclusivityDialog from "./AddExclusivityDialog";
import { ExclusivityFormValues } from "./AddExclusivityForm";
import {
  mapFormToCreateExclusivityPayload,
  mapFormToUpdateExclusivityPayload,
} from "./exclusivityMappers";
import ExclusivityTable from "./ExclusivityTable";

type Props = {
  isAddDialogOpen: boolean;
  onAddDialogClose: () => void;
};

const ExclusivitySection = ({ isAddDialogOpen, onAddDialogClose }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { talentId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [allExclusivities, setAllExclusivities] = useState<TalentExclusivity[]>(
    [],
  );
  const [exclusivityCursor, setExclusivityCursor] =
    useState<Nullable<string>>(null);
  const [exclusivityHits, setExclusivityHits] = useState<Hits>();
  const [editingExclusivity, setEditingExclusivity] =
    useState<TalentExclusivity | null>(null);
  const [exclusivityToDelete, setExclusivityToDelete] = useState<{
    id: string;
    version: number;
  } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isExclusivityTableOpen, setIsExclusivityTableOpen] = useState(true);
  const isEditDialogOpen = isAddDialogOpen || !!editingExclusivity;

  const fetchExclusivities = useCallback(
    async (cursor?: Nullable<string>) => {
      if (!talentId) return;
      setIsLoading(true);
      const result = await dispatch(
        fetchExclusivitiesByTalentId({
          talentId,
          pageSize: EXCLUSIVITIES_PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
        }),
      );
      setIsLoading(false);
      if (isFulfilled(result)) {
        setAllExclusivities((prev) =>
          cursor ? [...prev, ...result.payload.data] : result.payload.data,
        );
        setExclusivityCursor(result.payload.cursor ?? null);
        setExclusivityHits(result.payload.hits);
      }
    },
    [dispatch, talentId],
  );

  /**
   * Pagination is not used to keep ExclusivityTable and BlacklistTable visually consistent
   * Pass pagination to ExclusivityTable when BlacklistTable supports pagination
   * Disabling no-unused-vars until pagination is implemented
   */

  /* eslint-disable */
  const {
    pageData: exclusivityPageData,
    count,
    onPageChange,
    pageIndex,
    resetPagination,
  } = useTablePagination<TalentExclusivity>({
    data: allExclusivities,
    cursor: exclusivityCursor,
    hits: exclusivityHits,
    pageSize: EXCLUSIVITIES_PAGE_SIZE,
    fetchNext: (cursor) => fetchExclusivities(cursor),
    preventDispatch: true,
  });
  /* eslint-enable  */

  useEffect(() => {
    if (!talentId) return;
    fetchExclusivities();
  }, [talentId, fetchExclusivities]);

  const handleSaveExclusivity = (formData: ExclusivityFormValues) => {
    if (!talentId) return;

    if (editingExclusivity) {
      const body = mapFormToUpdateExclusivityPayload(formData, editingExclusivity);
      setIsLoading(true);
      dispatch(updateExclusivityEntry(body)).then((action) => {
        setIsLoading(false);
        if (isFulfilled(action)) {
          const updatedEvent = action.payload.find(
            (e) => e.name === "exEntryChg",
          );
          if (updatedEvent) {
            setAllExclusivities((prev) =>
              prev.map((entry) =>
                entry.id === editingExclusivity.id
                  ? {
                      ...entry,
                      customerOrganizationId: formData.customerOrganization?.id,
                      customerOrganizationName:
                        formData.customerOrganization?.name,
                      workLocationId: formData.workLocation?.id,
                      workLocationName: formData.workLocation?.name,
                      workTitles: formData.workTitles.map((wt) => ({
                        id: wt.id,
                        name: wt.name,
                      })),
                      version: updatedEvent.version,
                    }
                  : entry,
              ),
            );
          }
          handleCloseDialog();
        }
      });
    } else {
      const body = mapFormToCreateExclusivityPayload(formData, talentId);
      setIsLoading(true);
      dispatch(addExclusivityEntry(body)).then((action) => {
        setIsLoading(false);
        if (isFulfilled(action)) {
          const addedEvent = action.payload.find(
            (e) => e.name === "exEntryAdded",
          );
          if (addedEvent) {
            const newEntry: TalentExclusivity = {
              id: addedEvent.id,
              talentId,
              version: addedEvent.version,
              customerCompanyId: body.customerCompanyId,
              customerCompanyName: formData.customerCompany!.name,
              customerOrganizationId: formData.customerOrganization?.id,
              customerOrganizationName: formData.customerOrganization?.name,
              workLocationId: formData.workLocation?.id,
              workLocationName: formData.workLocation?.name,
              workTitles: formData.workTitles.map((wt) => ({
                id: wt.id,
                name: wt.name,
              })),
              createdAt: format(new Date(), isoFormat),
            };
            setAllExclusivities((prev) => [...prev, newEntry]);
            resetPagination();
          }
          handleCloseDialog();
        }
      });
    }
  };

  const handleEditExclusivity = (entry: TalentExclusivity) => {
    setEditingExclusivity(entry);
  };

  const handleDeleteExclusivityClick = (id: string, version: number) => {
    setExclusivityToDelete({ id, version });
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDeleteExclusivity = () => {
    if (!exclusivityToDelete) return;
    const body: RemoveExclusivityEntry = {
      exclusivityEntryId: exclusivityToDelete.id,
      version: exclusivityToDelete.version,
    };
    setIsLoading(true);
    dispatch(removeExclusivityEntry(body)).then((action) => {
      setIsLoading(false);
      if (isFulfilled(action)) {
        setAllExclusivities((prev) =>
          prev.filter((e) => e.id !== exclusivityToDelete.id),
        );
        setIsDeleteDialogOpen(false);
        setExclusivityToDelete(null);
      }
    });
  };

  const handleCloseDialog = () => {
    onAddDialogClose();
    setEditingExclusivity(null);
  };

  return (
    <Box mt={4}>
      <Typography variant="header" fontWeight={600} fontSize="20px" m={1}>
        {t("talentProfile:exclusivity")}
      </Typography>
      <ExclusivityTable
        exclusivities={allExclusivities}
        onEditClick={handleEditExclusivity}
        onDeleteClick={handleDeleteExclusivityClick}
        isLoading={isLoading}
        isCollapsed={!isExclusivityTableOpen}
        onToggleCollapse={() => setIsExclusivityTableOpen((prev) => !prev)}
      />

      <AddExclusivityDialog
        isOpen={isEditDialogOpen}
        editingEntry={editingExclusivity}
        onSave={handleSaveExclusivity}
        onClose={handleCloseDialog}
        isLoading={isLoading}
      />

      <ConfirmationDialog
        title={t("talentProfile:confirmExclusivityDelete")}
        contentText={t("talentProfile:confirmExclusivityDeleteContent")}
        cancelText={t("common:cancel")}
        confirmText={t("common:delete")}
        isOpen={isDeleteDialogOpen}
        onConfirm={handleConfirmDeleteExclusivity}
        onCancel={() => {
          setIsDeleteDialogOpen(false);
          setExclusivityToDelete(null);
        }}
      />
    </Box>
  );
};

export default ExclusivitySection;
